import * as React from 'react';

import { useWheel } from 'hooks/useWheel';

import DraggableSlideshowAsset from '../assets/DraggableSlideshowAsset';
import DraggableVideoAsset from '../assets/DraggableVideoAsset';
import LottieAsset from '../assets/LottieAsset';
import { RndAssetCallback } from '../assets/RndAsset';
import { useChildViewState, useNavigation } from '../context/NavigationContext';
import { useTemplateDispatch } from '../context/VideoTemplateDispatchContext';
import { useTemplateState } from '../context/VideoTemplateStateContext';
import { isDynamicAutoframedMediaLayer, isLottie } from '../state';
import useImageLockedFeatures from '../useImageLockedFeatures';
import useMediaLayer from '../useMediaLayer';
import useVideoClipLockedFeatures from '../useVideoClipLockedFeatures';
import { canvasBlock as block } from '../utils';

import Layer from './Layer';
import {
  DOUBLECLICK_CHANGE_TYPE_MAP,
  PLACEMENT_CHANGE_TYPE_MAP,
  WHEEL_CHANGE_TYPE_MAP,
} from './utils';

const { useCallback } = React;

export interface MediaLayerProps {
  layerId?: string;
  autoplayVideo?: boolean;
  useDefaultVideoStyle?: boolean;
}

const MediaLayer: React.FC<MediaLayerProps> = ({
  layerId,
  autoplayVideo = true,
  useDefaultVideoStyle = true,
}) => {
  const state = useTemplateState();
  const [navState] = useNavigation();
  const { service: childService } = useChildViewState();
  const dispatch = useTemplateDispatch();

  const mediaLayer = useMediaLayer({ layerId });

  // Lock check for double-click (fit/fill toggle) and wheel (scale) — both
  // placement edits that bypass RndAsset's drag/resize handles, so they need
  // their own gate. Drag/resize itself is gated inside the per-asset
  // components (DraggableSlideshowAsset, DraggableVideoAsset).
  const imageAssetId = mediaLayer.type === 'image' ? mediaLayer.id : '';
  const videoClipAssetId = mediaLayer.type === 'videoClip' ? mediaLayer.id : '';

  const imageLock = useImageLockedFeatures(imageAssetId);
  const videoClipLock = useVideoClipLockedFeatures(videoClipAssetId);
  const isMediaLocked = !!imageLock || !!videoClipLock;

  const handleLayerDoubleClick = useCallback(() => {
    // If media layer could not be initiaized or if the current layer its a
    // dynamic video clip, action is not dispatched.
    if (
      mediaLayer.type === 'none' ||
      isDynamicAutoframedMediaLayer(state, layerId) ||
      isMediaLocked
    ) {
      return;
    }

    const { id } = mediaLayer;
    dispatch({
      // adjust action type depending on image or videoclip
      type: DOUBLECLICK_CHANGE_TYPE_MAP[mediaLayer.type],
      payload: { id },
    });
  }, [dispatch, isMediaLocked, layerId, mediaLayer, state]);

  const handlePlacementChange: RndAssetCallback = useCallback(
    value => {
      // if media layer could not be initiaized, action is not dispatched
      if (mediaLayer.type === 'none') {
        return;
      }

      const { id } = mediaLayer;
      dispatch({
        // adjust action type depending on image or videoclip
        type: PLACEMENT_CHANGE_TYPE_MAP[mediaLayer.type],
        payload: {
          id,
          placement: value,
        },
      });
    },
    [dispatch, mediaLayer],
  );

  const handleLayerWheel = useWheel(
    useCallback(
      (e: React.WheelEvent<Element>) => {
        // if media layer could not be initiaized, action is not dispatched
        if (mediaLayer.type === 'none' || isMediaLocked) {
          return;
        }

        dispatch({
          // adjust action type depending on image or videoclip
          type: WHEEL_CHANGE_TYPE_MAP[mediaLayer.type],
          payload: {
            event: e,
            id: mediaLayer.id,
          },
        });
      },
      [dispatch, isMediaLocked, mediaLayer],
    ),
  );

  // this condition remains from when watermark was shown at ucs.
  // if it is brought back, this condition should be reviewed.
  const isLayerActive =
    navState.matches({ child: 'image' }) && childService?.state.matches('open');

  // asset is initialized with undefined, if type is not image or videoclip
  // no asset will be shown. this condition should not happen.
  let Asset;

  if (mediaLayer.type === 'image') {
    // for image types a draggableSlideshow/lottie component will be shown.
    const { id } = mediaLayer;
    Asset = isLottie(mediaLayer.asset) ? (
      <LottieAsset key={id} slideId={id} />
    ) : (
      <DraggableSlideshowAsset
        key={id}
        id={id}
        onDragStop={handlePlacementChange}
        onResizeStop={handlePlacementChange}
        params={id}
      />
    );
    // for video clips, a draggableVideo component will be shown.
  } else if (mediaLayer.type === 'videoClip') {
    const { id, asset } = mediaLayer;

    Asset = (
      <DraggableVideoAsset
        key={id}
        id={id}
        onDragStop={handlePlacementChange}
        onResizeStop={handlePlacementChange}
        params={id}
        autoplayVideo={autoplayVideo}
        useDefaultVideoStyle={useDefaultVideoStyle}
        enableBlur={asset?.enableBlur}
      />
    );
  }

  return (
    <Layer
      active={isLayerActive}
      className={block('media-layer')}
      onDoubleClick={handleLayerDoubleClick}
      onWheel={handleLayerWheel}
    >
      <>{Asset}</>
    </Layer>
  );
};

export default MediaLayer;
